import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as FilterUtil from 'search/FilterUtil';

import {BLASTFilter, Filter, FilterKey, GroupByKey, MultiValueFilter} from './SearchConstants';

const GROUP_KEYS_BY_TYPE: {[type: string]: Array<GroupByKey>} = {
  [ModelTypeConstants.CONTAINER]: [GroupByKey.CONTAINER_CONTENTS],
  [ModelTypeConstants.PLATE]: [GroupByKey.CONTAINER_CONTENTS],
  [ModelTypeConstants.BOX]: [GroupByKey.CONTAINER_CONTENTS],
  [ModelTypeConstants.LOCATION]: [GroupByKey.CONTAINER_CONTENTS],
  [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: [GroupByKey.RECIPE_RUN_TASK],
  [ModelTypeConstants.RECIPE_STEP_TASK]: [GroupByKey.RECIPE_METHOD_TASK],
  [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: [GroupByKey.ASSAY_RUN_TASK],
  [ModelTypeConstants.ASSAY_STEP_TASK]: [GroupByKey.ASSAY_METHOD_TASK],
  [ModelTypeConstants.TEST_ORDER]: [GroupByKey.CONTAINER, GroupByKey.SAMPLE],
};

export function narrowTypesForGroupKey(
  typesFilter: MultiValueFilter,
  groupKey?: GroupByKey | null
): MultiValueFilter {
  // Removes any unsupported types from a TYPES filter when setting a group by key.
  // Note that as of today we do not have to worry about selected schemas, as selecting a schema already
  // narrows to a single type. That will automatically restrict which group keys are available to select,
  // so we will never show a group key that is invalid for a given schema. If we add support to group by
  // schema fields, this will change, and we'll need to update this interaction. Given that we don't
  // know how we want to expose schema fields yet, we are not designing for that case here.

  assert(
    typesFilter.key === FilterKey.TYPES,
    `narrowTypesForGroupKey expects a TYPES filter, received ${typesFilter.key}`
  );

  // If we are removing a groupBy setting, any type is valid, so just leave the types alone.
  // If the groupKey is BLAST_QUERY, we don't restrict types, since the application of the BLASTFilter
  // already does that,  so also leave the types alone.
  if (!groupKey || groupKey === GroupByKey.BLAST_QUERY) {
    return typesFilter;
  }
  const newTypes = typesFilter.value.filter((type) => GROUP_KEYS_BY_TYPE[type]?.includes(groupKey));

  // Ensure our assumption about what group keys are available for selection is correct. If it is not,
  // the UI has a bug.
  assert(
    newTypes.length > 0,
    `Expected to have at least one selected type remaining for group key ${groupKey}, but none were. This likely means the Group By dropdown is showing a key that is invalid for the selected item type.`
  );

  return {...typesFilter, value: newTypes};
}

export function availableGroupKeysForFilters(filters: Array<Filter>): Array<GroupByKey> {
  const typesFilter = FilterUtil.getFilterByKey(filters, FilterKey.TYPES) as MultiValueFilter;
  const blastFilter = FilterUtil.getFilterByKey(filters, FilterKey.BLAST) as BLASTFilter | null;
  assert(Boolean(typesFilter), `availableGroupKeysForFilters expects a TYPES filter, but none was present`);

  const availableKeys = _.unique(typesFilter.value.flatMap((type) => GROUP_KEYS_BY_TYPE[type] ?? []));
  // If there is a BLASTFilter present with multiple queries, include the BLAST_QUERY group by key. This
  // doesn't do any TypesFilter checking since we want to support any type that the BLASTFilter is supported
  // for; its presence already implies that the types are supported.
  if (blastFilter && blastFilter.value.apiIdentifiers.length + blastFilter.value.textSequences.length > 1) {
    availableKeys.push(GroupByKey.BLAST_QUERY);
  }
  return availableKeys;
}
